import { useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang, LangToggle } from "@/components/site/LanguageContext";

const PATRIZIAS_LOGO =
  "https://images.getbento.com/accounts/78540d47b961291fe22c29ae3eb8a893/media/images/12400Patrizias-Brush.png?w=600&auto=compress,format";

const IMG = "https://static.prod-images.emergentagent.com/jobs/6918af59-ab46-445b-a012-e513807d76c0/images/";
const HERO = `${IMG}ee5a677f27227a93b88196f187c4a233f7751930a6e1cf846287cf2ab4066b1d.jpeg`;
const ART = "https://customer-assets-gfyr7b9c.emergentagent.net/job_mozzarella-show/artifacts/";

const seqSrcs = [
  { src: "/pizza/step-stesura.webp", wide: true },
  { src: "/pizza/step-mani.webp" },
  { src: "/pizza/step-volo.webp" },
  { src: "/pizza/step-pomodoro.webp" },
  { src: "/pizza/step-mozzarella.webp", wide: true },
  { src: "/pizza/step-versoforno.webp" },
  { src: "/pizza/step-pietra.webp", wide: true },
  { src: "/pizza/step-forno.webp" },
  { src: "/pizza/step-tocco.webp" },
  { src: "/pizza/step-finale.webp", hero: true },
];
const seqVideoSrcs = [
  { src: "/pizza/clip-stesura.mp4", poster: "/pizza/clip-stesura-poster.jpg" },
  { src: "/pizza/clip-condimento.mp4", poster: "/pizza/clip-condimento-poster.jpg" },
  { src: "/pizza/clip-infornata.mp4", poster: "/pizza/clip-infornata-poster.jpg" },
  { src: "/pizza/clip-uscita.mp4", poster: "/pizza/clip-uscita-poster.jpg" },
];
const signatureSrcs = [
  { src: `${ART}qiycdc2q_IMG_3766.webp`, wide: true },
  { src: "/pizza/signature-presenta.webp" },
];
const menuSrcs = ["/pizza/step-forno.webp", "/pizza/step-finale.webp", `${ART}qiycdc2q_IMG_3766.webp`];

const copy = {
  en: {
    backHub: "Back to Hub",
    backSite: "Back to site",
    phase: "Phase 2 · Concept",
    heroKicker: "Coming after the Show",
    heroBody: (brand) => `Roman pinsa, live fire, and mozzarella born at your table. A brand-new pizza style — lighter, bolder, and unforgettable, from the ${brand} concept.`,
    heroBodyPizza: "Long-fermented pizza, live fire, and mozzarella born at your table. Lighter, bolder, and unforgettable — from the Filata concept.",
    conceptPizza0: { t: "Long-Fermented Pizza", d: "Long-fermented, light and crisp — a contemporary dough with rare digestibility. A refined base for the mozzarella ritual." },
    concept: [
      { t: "Pinsa Romana", d: "Long-fermented, light and crisp — a contemporary style few venues own. A refined base for the mozzarella ritual." },
      { t: "Show → Slice", d: "Stracciatella and mozzarella pulled by hand and laid on the hot pinsa right at your table." },
      { t: "Signature flavors", d: "Hot honey, Calabrian chili and a house twist — bold, shareable, made to order." },
      { t: "Fire & theatre", d: "Open oven, scissor-cut service, warm ambient light — a moment made for sharing." },
    ],
    seqKicker: "The Live Sequence · One Evening, For Real",
    seqH2: <>From the dough to the fire, <em className="italic">to the pizza.</em></>,
    seqBody: "Shot during a real evening — every pizza finished with the mozzarella produced live at the show, minutes earlier.",
    steps: [
      { t: "The stesura", d: "Floured steel, warm dough, open hands — the base takes shape." },
      { t: "On the fists", d: "Stretched in the air, the old way — thin at the centre, alive at the edge." },
      { t: "The flight", d: "A pass, a turn — the dough breathes before the fire." },
      { t: "San Marzano", d: "Tomato laid in a spiral, spoon by spoon." },
      { t: "Our own mozzarella", d: "Cut by hand from the mozzarella produced live at the show, minutes earlier — the heart of the concept." },
      { t: "Towards the fire", d: "Carried on the hands, straight to the mouth of the oven." },
      { t: "On the stone", d: "The crust lifts, the mozzarella melts — minutes of pure anticipation." },
      { t: "From the oven", d: "On the peel — blistered crust, molten heart." },
      { t: "The final touch", d: "Crudo laid warm, rocket leaf by leaf." },
      { t: "The pizza", d: "Crudo, rocket and our warm mozzarella — straight to the table." },
    ],
    seqVideos: ["The stesura, live", "The topping, onto the peel", "Into the fire", "Out of the oven, finished"],
    signatures: ["Mortadella, our burrata & pistachio", "Crudo, rocket & warm stracciatella — presented by the pizzaiolo"],
    menuKicker: "The Menu · Baked That Evening",
    menuH2: <>The pizzas, <em className="italic">for real.</em></>,
    menuBody: "No renders, no stock — these are the pizzas of the evening, each finished with the mozzarella made at the show.",
    menu: [
      { n: "Margherita al Momento", d: "San Marzano tomato, our mozzarella cut by hand, basil, cold-pressed olive oil" },
      { n: "Crudo & Rucola", d: "Prosciutto crudo laid warm, wild rocket, dollops of our stracciatella" },
      { n: "Mortadella & Pistacchio", d: "Mortadella, a whole heart of our burrata, pistachio pesto" },
    ],
    endKicker: "End of the presentation",
    endH2: <>That's the full concept — <em className="italic">thank you.</em></>,
    endBody: "Take the one-pager with you, or go back to the beginning.",
    endPdf: "Download the one-pager (PDF)",
    endHub: "Back to the Hub",
  },
  it: {
    backHub: "Torna alla Hub",
    backSite: "Torna al sito",
    phase: "Fase 2 · Concept",
    heroKicker: "Dopo lo Show",
    heroBody: (brand) => `Pinsa romana, fuoco vivo e mozzarella nata al tuo tavolo. Uno stile di pizza tutto nuovo — più leggero, più audace, indimenticabile, dal concept ${brand}.`,
    heroBodyPizza: "Pizza a lunga fermentazione, fuoco vivo e mozzarella nata al tuo tavolo. Più leggera, più audace, indimenticabile — dal concept Filata.",
    conceptPizza0: { t: "Pizza a Lunga Fermentazione", d: "Lunga fermentazione, leggera e croccante — un impasto contemporaneo di rara digeribilità. Una base raffinata per il rituale della mozzarella." },
    concept: [
      { t: "Pinsa Romana", d: "Lunga fermentazione, leggera e croccante — uno stile contemporaneo che pochi locali possiedono. Una base raffinata per il rituale della mozzarella." },
      { t: "Show → Fetta", d: "Stracciatella e mozzarella filate a mano e adagiate sulla pinsa calda direttamente al tavolo." },
      { t: "Sapori d'autore", d: "Miele piccante, peperoncino calabrese e un tocco della casa — audaci, da condividere, fatti al momento." },
      { t: "Fuoco & teatro", d: "Forno a vista, servizio con le forbici, luce calda — un momento fatto per essere condiviso." },
    ],
    seqKicker: "La Sequenza dal Vivo · Una Serata, Per Davvero",
    seqH2: <>Dalla stesura al forno, <em className="italic">fino alla pizza.</em></>,
    seqBody: "Fotografata durante una serata reale — ogni pizza finita con la mozzarella prodotta dal vivo allo show, pochi minuti prima.",
    steps: [
      { t: "La stesura", d: "Acciaio infarinato, impasto caldo, mani aperte — la base prende forma." },
      { t: "Sui pugni", d: "Allargata in aria, alla vecchia maniera — sottile al centro, viva sul bordo." },
      { t: "Il volo", d: "Un passaggio, un giro — l'impasto respira prima del fuoco." },
      { t: "San Marzano", d: "Il pomodoro steso a spirale, cucchiaio dopo cucchiaio." },
      { t: "La nostra mozzarella", d: "Tagliata a mano dalla mozzarella prodotta dal vivo allo show, pochi minuti prima — il cuore del concept." },
      { t: "Verso il fuoco", d: "Portata sulle mani, dritta alla bocca del forno." },
      { t: "Sulla pietra", d: "Il cornicione si alza, la mozzarella si scioglie — minuti di pura attesa." },
      { t: "Dal forno", d: "Sulla pala — cornicione maculato, cuore fuso." },
      { t: "Il tocco finale", d: "Il crudo adagiato a caldo, la rucola foglia per foglia." },
      { t: "La pizza", d: "Crudo, rucola e la nostra mozzarella calda — dritta al tavolo." },
    ],
    seqVideos: ["La stesura, dal vivo", "Il condimento, sulla pala", "L'infornata", "L'uscita dal forno, condita"],
    signatures: ["Mortadella, la nostra burrata e pistacchio", "Crudo, rucola e stracciatella calda — presentata dal pizzaiolo"],
    menuKicker: "Il Menu · Sfornato Quella Sera",
    menuH2: <>Le pizze, <em className="italic">dal vero.</em></>,
    menuBody: "Nessun render, nessuna foto di repertorio — queste sono le pizze della serata, ognuna finita con la mozzarella fatta allo show.",
    menu: [
      { n: "Margherita al Momento", d: "Pomodoro San Marzano, la nostra mozzarella tagliata a mano, basilico, olio a crudo" },
      { n: "Crudo & Rucola", d: "Prosciutto crudo adagiato a caldo, rucola selvatica, ciuffi della nostra stracciatella" },
      { n: "Mortadella & Pistacchio", d: "Mortadella, un cuore intero della nostra burrata, pesto di pistacchio" },
    ],
    endKicker: "Fine della presentazione",
    endH2: <>Questo è tutto il concept — <em className="italic">grazie.</em></>,
    endBody: "Porta con te il one-pager, oppure torna all'inizio.",
    endPdf: "Scarica il one-pager (PDF)",
    endHub: "Torna alla Hub",
  },
  ru: {
    backHub: "К хабу",
    backSite: "На сайт",
    phase: "Фаза 2 · Концепт",
    heroKicker: "После шоу",
    heroBody: (brand) => `Римская пинса, живой огонь и моцарелла, рождённая у вашего стола — из концепта ${brand}.`,
    heroBodyPizza: "Пицца длительной ферментации, живой огонь и моцарелла, рождённая у вашего стола — из концепта Filata.",
    conceptPizza0: { t: "Пицца длительной ферментации", d: "Длительная ферментация, лёгкая и хрустящая — современное тесто редкой лёгкости. Утончённая база для ритуала моцареллы." },
    concept: [
      { t: "Pinsa Romana", d: "Длительная ферментация, лёгкая и хрустящая — современный стиль, который есть у немногих. Утончённая база для ритуала моцареллы." },
      { t: "Шоу → Кусок", d: "Страчателла и моцарелла, вытянутые вручную и выложенные на горячую основу прямо у стола." },
      { t: "Фирменные вкусы", d: "Острый мёд, калабрийский перец и авторский штрих — ярко, к столу, по заказу." },
      { t: "Огонь и театр", d: "Открытая печь, подача с ножницами, тёплый свет — момент, созданный для того, чтобы делиться." },
    ],
    seqKicker: "Живая последовательность · Один вечер, по-настоящему",
    seqH2: <>От теста к огню, <em className="italic">до пиццы.</em></>,
    seqBody: "Снято в настоящий вечер — каждая пицца завершена моцареллой, произведённой вживую на шоу за несколько минут до этого.",
    steps: [
      { t: "Раскатка", d: "Мука, тёплое тесто, открытые ладони — основа обретает форму." },
      { t: "На кулаках", d: "Растянуто в воздухе, по-старинке — тонко в центре, живо по краю." },
      { t: "Полёт", d: "Пас, оборот — тесто дышит перед огнём." },
      { t: "Сан-Марцано", d: "Томат по спирали, ложка за ложкой." },
      { t: "Наша моцарелла", d: "Нарезана вручную из моцареллы, произведённой на шоу минутами ранее — сердце концепта." },
      { t: "К огню", d: "На руках, прямо к жерлу печи." },
      { t: "На камне", d: "Корка поднимается, моцарелла тает — минуты чистого ожидания." },
      { t: "Из печи", d: "На лопате — пузыристая корка, расплавленное сердце." },
      { t: "Финальный штрих", d: "Прошутто на тёплое, руккола лист за листом." },
      { t: "Пицца", d: "Крудо, руккола и наша тёплая моцарелла — прямо к столу." },
    ],
    seqVideos: ["Раскатка, вживую", "Начинка, на лопату", "В печь", "Из печи, с начинкой"],
    signatures: ["Мортаделла, наша буррата и фисташка", "Крудо, руккола и тёплая страчателла — от пиццайоло"],
    menuKicker: "Меню · Испечено в тот вечер",
    menuH2: <>Пиццы, <em className="italic">по-настоящему.</em></>,
    menuBody: "Никаких рендеров — это пиццы того вечера, каждая с моцареллой, сделанной на шоу.",
    menu: [
      { n: "Margherita al Momento", d: "Томат Сан-Марцано, наша моцарелла, базилик, масло первого отжима" },
      { n: "Crudo & Rucola", d: "Прошутто крудо на тёплое, дикая руккола, наша страчателла" },
      { n: "Mortadella & Pistacchio", d: "Мортаделла, целое сердце нашей бурраты, фисташковый песто" },
    ],
    endKicker: "Конец презентации",
    endH2: <>Это весь концепт — <em className="italic">спасибо.</em></>,
    endBody: "Возьмите one-pager с собой или вернитесь к началу.",
    endPdf: "Скачать one-pager (PDF)",
    endHub: "К хабу",
  },
  es: {
    backHub: "Volver al Hub",
    backSite: "Volver al sitio",
    phase: "Fase 2 · Concepto",
    heroKicker: "Después del Show",
    heroBody: (brand) => `Pinsa romana, fuego vivo y mozzarella nacida en tu mesa. Un estilo de pizza totalmente nuevo — más ligero, más audaz, inolvidable, del concepto ${brand}.`,
    heroBodyPizza: "Pizza de larga fermentación, fuego vivo y mozzarella nacida en tu mesa. Más ligera, más audaz, inolvidable — del concepto Filata.",
    conceptPizza0: { t: "Pizza de Larga Fermentación", d: "Larga fermentación, ligera y crujiente — una masa contemporánea de rara digestibilidad. Una base refinada para el ritual de la mozzarella." },
    concept: [
      { t: "Pinsa Romana", d: "Larga fermentación, ligera y crujiente — un estilo contemporáneo que pocos locales poseen. Una base refinada para el ritual de la mozzarella." },
      { t: "Show → Porción", d: "Stracciatella y mozzarella hiladas a mano y colocadas sobre la base caliente directamente en tu mesa." },
      { t: "Sabores de autor", d: "Miel picante, chile calabrés y un toque de la casa — audaces, para compartir, hechos al momento." },
      { t: "Fuego & teatro", d: "Horno a la vista, servicio con tijeras, luz cálida — un momento hecho para compartir." },
    ],
    seqKicker: "La Secuencia en Vivo · Una Noche, De Verdad",
    seqH2: <>De la masa al fuego, <em className="italic">hasta la pizza.</em></>,
    seqBody: "Fotografiada durante una noche real — cada pizza terminada con la mozzarella producida en vivo en el show, minutos antes.",
    steps: [
      { t: "El estirado", d: "Acero enharinado, masa tibia, manos abiertas — la base toma forma." },
      { t: "Sobre los puños", d: "Estirada en el aire, a la vieja usanza — fina en el centro, viva en el borde." },
      { t: "El vuelo", d: "Un pase, un giro — la masa respira antes del fuego." },
      { t: "San Marzano", d: "El tomate extendido en espiral, cuchara a cuchara." },
      { t: "Nuestra mozzarella", d: "Cortada a mano de la mozzarella producida en vivo en el show, minutos antes — el corazón del concepto." },
      { t: "Hacia el fuego", d: "Llevada en las manos, directo a la boca del horno." },
      { t: "Sobre la piedra", d: "El borde se levanta, la mozzarella se derrite — minutos de pura espera." },
      { t: "Del horno", d: "Sobre la pala — borde moteado, corazón fundido." },
      { t: "El toque final", d: "El crudo colocado en caliente, la rúcula hoja por hoja." },
      { t: "La pizza", d: "Crudo, rúcula y nuestra mozzarella tibia — directo a la mesa." },
    ],
    seqVideos: ["El estirado, en vivo", "El condimento, sobre la pala", "Al horno", "Del horno, terminada"],
    signatures: ["Mortadela, nuestra burrata y pistacho", "Crudo, rúcula y stracciatella tibia — presentada por el pizzaiolo"],
    menuKicker: "El Menú · Horneado Esa Noche",
    menuH2: <>Las pizzas, <em className="italic">de verdad.</em></>,
    menuBody: "Sin renders, sin fotos de archivo — estas son las pizzas de la noche, cada una terminada con la mozzarella hecha en el show.",
    menu: [
      { n: "Margherita al Momento", d: "Tomate San Marzano, nuestra mozzarella cortada a mano, albahaca, aceite en crudo" },
      { n: "Crudo & Rucola", d: "Prosciutto crudo colocado en caliente, rúcula silvestre, copos de nuestra stracciatella" },
      { n: "Mortadella & Pistacchio", d: "Mortadela, un corazón entero de nuestra burrata, pesto de pistacho" },
    ],
    endKicker: "Fin de la presentación",
    endH2: <>Este es todo el concepto — <em className="italic">gracias.</em></>,
    endBody: "Llévate el one-pager, o vuelve al inicio.",
    endPdf: "Descargar el one-pager (PDF)",
    endHub: "Volver al Hub",
  },
};

export const PinsaChapter = ({ isPat = false, flavor = "pinsa" }) => {
  const { lang } = useLang();
  const t = copy[lang];
  const pizza = flavor === "pizza";
  const conceptItems = pizza
    ? [t.conceptPizza0, ...t.concept.slice(1).map((c) => ({ ...c, d: c.d.replace(/pinsa/g, "pizza") }))]
    : t.concept;
  return (
    <>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-8 md:pt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-5">{t.heroKicker}</p>
          <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight">{pizza ? "Pizza" : "Pinsa"}<br /><em className="italic">&amp; Fuoco</em></h1>
          <p className="font-sans text-base md:text-lg text-bg/70 max-w-md mt-8 leading-relaxed">
            {pizza ? t.heroBodyPizza : t.heroBody(isPat ? "Patrizia\u2019s" : "Filata")}
          </p>
        </div>
        <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
          <img src={HERO} alt="Roman pinsa, dark and moody" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Concept */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 border-t border-bg/10 mt-16">
        {conceptItems.map((c, i) => (
          <div key={i} className="flex gap-5">
            <span className="font-serif italic text-4xl text-terracotta/80 leading-none">0{i + 1}</span>
            <div>
              <h3 className="font-serif text-2xl md:text-3xl mb-2">{c.t}</h3>
              <p className="font-sans text-bg/70 leading-relaxed max-w-md">{c.d}</p>
            </div>
          </div>
        ))}
      </section>

      {/* The live sequence */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-8" data-testid="pizza-sequence">
        <div className="border-t border-bg/10 pt-16 md:pt-20 mb-10 md:mb-14 max-w-2xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-5">{t.seqKicker}</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.02]">{t.seqH2}</h2>
          <p className="font-sans text-sm md:text-base text-bg/60 mt-6 leading-relaxed">{t.seqBody}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {seqSrcs.map((s, i) => (
            <figure
              key={s.src}
              data-testid={`sequence-step-${String(i + 1).padStart(2, "0")}`}
              className={`group relative overflow-hidden rounded-sm ${
                s.hero ? "col-span-2 lg:col-span-4 aspect-[16/9] md:aspect-[21/9]" : s.wide ? "col-span-2 aspect-[16/9] lg:aspect-auto" : "aspect-[3/4]"
              }`}
            >
              <img src={s.src} alt={t.steps[i].t} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
              <span className="absolute top-4 left-4 font-serif italic text-3xl md:text-4xl text-bg/90 leading-none drop-shadow">{String(i + 1).padStart(2, "0")}</span>
              <figcaption className="absolute inset-x-0 bottom-0 p-4">
                <span className="font-serif text-lg md:text-xl text-bg block">{t.steps[i].t}</span>
                <span className="font-sans text-xs md:text-sm text-bg/70 leading-snug block mt-1">{t.steps[i].d}</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-4 md:mt-5" data-testid="sequence-videos">
          {seqVideoSrcs.map((v, i) => (
            <figure key={v.src} className="relative overflow-hidden rounded-sm aspect-[4/3]">
              <video src={v.src} poster={v.poster} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <span className="font-serif italic text-bg text-base md:text-lg">{t.seqVideos[i]}</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-4 md:mt-5" data-testid="signature-pizzas">
          {signatureSrcs.map((s, i) => (
            <figure key={s.src} className={`group relative overflow-hidden rounded-sm ${s.wide ? "md:col-span-2 aspect-[16/9] md:aspect-auto" : "aspect-[3/4]"}`}>
              <img src={s.src} alt={t.signatures[i]} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4">
                <span className="font-serif italic text-bg text-base md:text-xl">{t.signatures[i]}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* The real menu */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-16 pb-24" data-testid="pizza-menu">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-12 border-t border-bg/10 pt-16">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-4">{t.menuKicker}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.02] max-w-xl">{t.menuH2}</h2>
          </div>
          <p className="font-sans text-sm text-bg/60 max-w-xs">{t.menuBody}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {t.menu.map((m, i) => (
            <div key={m.n} data-testid={`menu-pizza-${i + 1}`}>
              <figure className="group relative overflow-hidden rounded-sm aspect-[4/3]">
                <img src={menuSrcs[i]} alt={m.n} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              </figure>
              <div className="mt-5 flex items-baseline gap-3">
                <span className="font-serif italic text-2xl text-terracotta/80 leading-none">{["I", "II", "III"][i]}</span>
                <h3 className="font-serif text-2xl md:text-3xl">{m.n}</h3>
              </div>
              <p className="font-sans text-sm text-bg/70 leading-relaxed mt-2 pl-8">{m.d}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default function Pizzeria() {
  const [params] = useSearchParams();
  const isPat = params.get("brand") === "patrizias";
  const { lang } = useLang();
  const t = copy[lang];

  return (
    <div className="min-h-screen bg-wine text-bg" data-testid="pizzeria-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="max-w-[1200px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-4">
        <a href={isPat ? "/hub" : "/"} data-testid="pizzeria-back" className="inline-flex items-center gap-2 font-sans text-sm text-bg/70 hover:text-bg transition-colors">
          <ArrowLeft className="h-4 w-4" /> {isPat ? t.backHub : t.backSite}
        </a>
        {isPat && <img src={PATRIZIAS_LOGO} alt="Patrizia's" className="h-11 md:h-12 w-auto" />}
        <div className="flex items-center gap-5">
          <LangToggle variant="dark" />
          <span className="hidden sm:block font-sans text-[11px] uppercase tracking-[0.3em] text-bg/50">{t.phase}</span>
        </div>
      </header>
      <PinsaChapter isPat={isPat} />

      {isPat && (
        <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-24 -mt-6" data-testid="presentation-end">
          <div className="border border-bg/15 rounded-sm bg-[#120a0c] p-8 md:p-12 text-center">
            <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-terracotta mb-4">{t.endKicker}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">{t.endH2}</h2>
            <p className="font-sans text-sm md:text-base text-bg/60 mt-4">{t.endBody}</p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/patrizias-onepager.pdf"
                download="Patrizias-Live-Mozzarella-Experience.pdf"
                data-testid="end-pdf-link"
                className="rounded-full bg-terracotta px-8 py-4 text-sm font-sans tracking-wide text-white hover:bg-terracotta/90 transition-colors"
              >
                {t.endPdf}
              </a>
              <a href="/hub" data-testid="end-hub-link" className="font-sans text-sm text-bg/70 underline underline-offset-4 decoration-bg/30 hover:text-bg transition-colors">
                {t.endHub}
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
